import clsx from 'clsx';
import { Button, Empty, Skeleton } from 'antd';

import Comment from './Comment';
import { Comment as CommentType } from '../../../../store/comment';
import { Reaction } from '../../../../store/types';

import styles from './index.module.scss';

interface CommentsListProps {
  data: CommentType[];
  ownerId?: string;
  setData: React.Dispatch<React.SetStateAction<CommentType[]>>;
  loading: boolean;
  parentId?: string;
  onLoadMore: () => void;
  hasNextPage: boolean;
  onRepplyDeleted?: (parentId: string) => void;
  onCommentDeleted?: () => void;
}

const CommentsList = ({
  data,
  ownerId,
  setData,
  loading,
  parentId,
  onLoadMore,
  hasNextPage,
  onRepplyDeleted,
  onCommentDeleted,
}: CommentsListProps) => {
  const onRepplyAdded = (commentId: string) => {
    setData((prev) =>
      prev.map((item) =>
        item.id === commentId
          ? {
              ...item,
              numberOfCommentsTotal: item.numberOfCommentsTotal + 1,
            }
          : item
      )
    );
  };

  const onCommentDelete = (commentId: string) => {
    setData((prev) => prev.filter((item) => item.id !== commentId));
    onCommentDeleted?.();
  };

  const onReactionChange = (commentId: string, reaction: Reaction | null) => {
    setData((prev) =>
      prev.map((item) => {
        if (item.id !== commentId) return item;

        const prevReaction: Reaction | null = item.reactions[0] || null;
        let numberOfLikes = item.numberOfLikes;
        let numberOfDislikes = item.numberOfDislikes;

        if (!prevReaction && reaction) {
          if (reaction.status) {
            numberOfLikes += 1;
          } else {
            numberOfDislikes += 1;
          }
        } else if (prevReaction && !reaction) {
          if (prevReaction.status) {
            numberOfLikes -= 1;
          } else {
            numberOfDislikes -= 1;
          }
        } else if (prevReaction && reaction) {
          if (prevReaction.status !== reaction.status) {
            if (reaction.status) {
              numberOfLikes += 1;
              numberOfDislikes -= 1;
            } else {
              numberOfLikes -= 1;
              numberOfDislikes += 1;
            }
          }
        }

        return {
          ...item,
          numberOfLikes,
          numberOfDislikes,
          reactions: reaction ? [reaction] : [],
        };
      })
    );
  };

  if (!data.length && loading) {
    return <Skeleton />;
  }

  if (!data || !data.length) {
    return (
      <Empty
        className={clsx(!parentId && styles.wrapper)}
        description={parentId ? 'No replies yet' : 'No comments yet'}
      />
    );
  }

  return (
    <div className={clsx(!parentId && styles.wrapper)}>
      {data.map((comment) => (
        <Comment
          isOwner={ownerId === comment.user?.id}
          ownerId={ownerId}
          key={comment.id}
          comment={comment}
          hideReply={!!parentId}
          onRepplyAdded={() => onRepplyAdded?.(comment.id)}
          onRepplyDeleted={() => onRepplyDeleted?.(comment.id)}
          onReactionChange={(reactions) =>
            onReactionChange(comment.id, reactions)
          }
          onCommentDeleted={() => onCommentDelete(comment.id)}
          onEdited={({ text, files }) => {
            setData((prev) =>
              prev.map((item) =>
                item.id === comment.id ? { ...item, text, files } : item
              )
            );
          }}
        />
      ))}

      {hasNextPage && (
        <div className={styles.pagination}>
          <Button
            type="text"
            onClick={onLoadMore}
            disabled={loading}
            className={styles.loadMore}
          >
            See more
          </Button>
        </div>
      )}
    </div>
  );
};

export default CommentsList;
